import * as fe from "@runn/filter-engine"
import React, { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { match } from "ts-pattern"

import styles from "./Main.module.css"

import {
  AllowedProjectFilter,
  FilterableProject,
  prepareDataForFilterPersonList,
  prepareDataForFilterProjectList,
} from "~/helpers/filter-engine"
import { convertToProjectFilterList } from "~/helpers/filter-helpers"
import { filterPeopleByContracts } from "~/helpers/filterPeopleByContracts"
import { getVisiblePlaceholders } from "~/helpers/placeholder-helpers"
import { pluralize } from "~/helpers/plural"
import { SCROLLBAR_WIDTH } from "~/helpers/scrollbar"

import CalendarOutline from "~/common/calendar/CalendarOutline"
import Timeline from "~/common/timespan/Timeline"

import { usePeopleFilterSet } from "~/PeoplePlanner/usePeopleFilterSet"
import usePeoplePlannerFilterSet from "~/PeoplePlanner/usePeoplePlannerFilterSet"
import { Chart, Legend } from "~/Planner/ChartPanel/Chart"
import type {
  ChartPeople,
  ChartPeriod,
  ChartProjects,
  ChartType,
} from "~/Planner/ChartPanel/types"
import NoResultsForFilter from "~/Planner/NoResultsForFilter"
import { setPeopleFilterSet } from "~/Planner/Planner.actions"
import { isTentativeProjectEnabled } from "~/Planner/reducer2/scenarioPlanningSlice"
import useProjectPlannerFilters from "~/ProjectPlanner/useProjectPlannerFilters"
import { useAppSelector } from "~/hooks/redux"
import { ReduxState } from "~/rootReducer"

type Props = {
  enableAnimations: boolean
  favouritePersonIds: number[]
  favouriteProjectIds: number[]
  allPeople: ChartPeople
  allProjects: ChartProjects

  chartType: ChartType
  chartPeriod: ChartPeriod

  companyDefaultMinutes: number
  accountHasViews: boolean

  viewProjectFilters: AllowedProjectFilter | undefined
}

/**
 * We want to highlight the projects that match the currently applied
 * people project filters in the chart. To do that, converting the
 * people project filters to project filters and running it through
 * our project filter engine and returning the projects that match
 * the filters.
 *
 * This is only applicable in the people planner (context: CYCLE-1109)
 */
const useFilteredProjects = (
  projectPreparedList: FilterableProject[],
  allProjects: ChartProjects,
) => {
  const peopleFilterSet = usePeopleFilterSet()
  const inPeoplePlanner =
    useSelector((state: ReduxState) => state.activePage) === "people"

  const projectFilterSet = useMemo(() => {
    return inPeoplePlanner
      ? {
          ...peopleFilterSet,
          filters: convertToProjectFilterList(peopleFilterSet.filters),
        }
      : null
  }, [peopleFilterSet, inPeoplePlanner])

  const projectFilterCount = useMemo(
    () => (projectFilterSet ? fe.filters.length(projectFilterSet.filters) : 0),
    [projectFilterSet],
  )

  const filteredProjects = useMemo(() => {
    if (projectFilterCount === 0) {
      return null
    }

    const filtered = new Set(
      fe.engines.local
        .filterProjectList(projectPreparedList, projectFilterSet)
        .map((project) => project.id),
    )

    return allProjects.filter((project) => filtered.has(project.id))
  }, [projectPreparedList, allProjects, projectFilterSet, projectFilterCount])

  return { filteredProjects, projectFilterCount }
}

const ChartMain = (props: Props) => {
  const {
    enableAnimations,
    favouritePersonIds,
    favouriteProjectIds,
    allProjects,
    allPeople,
    chartType,
    chartPeriod,
    companyDefaultMinutes,
    viewProjectFilters,
  } = props

  const dispatch = useDispatch()

  const calendarStartDate = useSelector(
    (state: ReduxState) => state.calendar.calendarStartDate,
  )

  const calendarEndDate = useSelector(
    (state: ReduxState) => state.calendar.calendarEndDate,
  )

  const includeWeekends = useSelector(
    (state: ReduxState) => state.calendar.calendarWeekendsExpanded,
  )

  const calStartNum = useSelector(
    (state: ReduxState) => state.calendar.calStartNum,
  )

  const calEndNum = useAppSelector((state) => state.calendar.calEndNum)

  const enabledTentativeProjects = useAppSelector(
    (state) => state.plannerV2.scenarioPlanning.enabledTentativeProjects,
  )

  const enabledProjects = allProjects.filter(
    ({ confirmed, is_template, id }) =>
      confirmed ||
      isTentativeProjectEnabled(enabledTentativeProjects, id) ||
      is_template,
  )

  const projectsFilterSet = useProjectPlannerFilters(viewProjectFilters)

  const projectPreparedList = useMemo(() => {
    const filterTypeList = fe.filters.asTypeList(projectsFilterSet.filters)
    return prepareDataForFilterProjectList({
      filterTypeList,
      projects: allProjects,
      people: allPeople,
      favouriteProjectIds,
      calStartNum,
    })
  }, [
    projectsFilterSet.filters,
    allProjects,
    allPeople,
    favouriteProjectIds,
    calStartNum,
  ])

  const filterSet = usePeoplePlannerFilterSet(
    projectsFilterSet,
    projectPreparedList,
  )
  const filterTypeList = fe.filters.asTypeList(filterSet.filters)

  // Can't use usePeoplePlannerFilters() here since it includes view filters
  const explicitFilterSet = usePeopleFilterSet()
  const hasFilters = fe.filters.length(explicitFilterSet.filters) > 0

  const preparedList = useMemo(
    () =>
      prepareDataForFilterPersonList({
        filterTypeList,
        projects: enabledProjects,
        people: allPeople,
        favouritePersonIds,
        calStartNum,
        includeArchivedProjects: true,
      }),
    [
      filterTypeList,
      enabledProjects,
      allPeople,
      favouritePersonIds,
      calStartNum,
    ],
  )

  const filteredPeople = useMemo(() => {
    const filteredPersonList = fe.engines.local.filterPersonList(
      preparedList,
      filterSet,
    )

    const filteredPersonIds = Object.fromEntries(
      filteredPersonList.map((person) => [person.id, true]),
    )

    const filtered = allPeople.filter((person) => filteredPersonIds[person.id])

    const people = filtered.filter((p) => !p.is_placeholder)

    const filteredPeopleByContracts = filterPeopleByContracts(people, filterSet)

    const placeholders = getVisiblePlaceholders(
      filtered.filter((p) => p.is_placeholder),
      allProjects,
      calStartNum,
      calEndNum,
    ) as any

    return [...filteredPeopleByContracts, ...placeholders]
  }, [preparedList, filterSet, allPeople, allProjects, calStartNum, calEndNum])

  const hasPeopleResults = Boolean(filteredPeople.length)

  const peopleDataMessage = match(hasPeopleResults)
    .with(false, () => ``)
    .otherwise(() => {
      const peopleSortedByType = filteredPeople.reduce(
        (acc, person) => {
          person.is_placeholder
            ? (acc.placeholders = acc.placeholders + 1)
            : (acc.people = acc.people + 1)

          return acc
        },
        { people: 0, placeholders: 0 },
      )

      const { people, placeholders } = peopleSortedByType

      return (
        <div>
          {"Displaying data for "}
          {people ? `${people} ${pluralize(people, "person", "people")}` : ""}
          {people && placeholders ? (
            <span>
              {" and "}
              <br />
            </span>
          ) : (
            ""
          )}
          {placeholders
            ? `${placeholders} ${pluralize(placeholders, "placeholder")}`
            : ""}
        </div>
      )
    })

  const includeTentative = enabledTentativeProjects.length > 0

  const { filteredProjects, projectFilterCount } = useFilteredProjects(
    projectPreparedList,
    allProjects,
  )

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <div className={styles.info}>
          {peopleDataMessage && <b>{peopleDataMessage}</b>}
          {!hasPeopleResults && (
            <NoResultsForFilter
              subject="people"
              minimal
              hasFilters={hasFilters}
              onClearFilters={() => {
                dispatch(setPeopleFilterSet({ name: null, filters: null }))
              }}
            />
          )}
        </div>
        <Legend
          type={chartType}
          includeTentative={includeTentative}
          appliedProjectFilterCount={projectFilterCount}
        />
      </div>
      <div className={styles.graphContainer}>
        <div className={styles.chart} style={{ marginRight: SCROLLBAR_WIDTH }}>
          <CalendarOutline type="standard" />
          <Chart
            type={chartType}
            people={filteredPeople}
            projects={enabledProjects}
            filteredProjects={filteredProjects}
            startDate={calendarStartDate}
            endDate={calendarEndDate}
            period={chartPeriod}
            includeWeekends={includeWeekends}
            enableAnimations={enableAnimations}
            companyDefaultMinutes={companyDefaultMinutes}
            includeTentative={includeTentative}
            appliedProjectFilterCount={projectFilterCount}
          />
        </div>
        <Timeline monthsBelow />
      </div>
    </div>
  )
}

export { ChartMain }
